import { useState } from 'react';
import emailjs from '@emailjs/browser';
import { useTranslation } from '../../../i18n/TranslationContext';

const emptyContactForm = {
  name: '',
  email: '',
  phone: '',
  datetime: '',
  service: '',
};

type ContactStatus = 'idle' | 'sending' | 'success' | 'error';
type ContactFormValues = typeof emptyContactForm;

export default function ContactSection() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ContactStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [formValues, setFormValues] = useState<ContactFormValues>(emptyContactForm);

  const handleChange = (field: keyof ContactFormValues, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = (form: HTMLFormElement) => {
    setFormValues(emptyContactForm);
    form.reset();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formPayload = new FormData(form);
    const honeypot = String(formPayload.get('company_alt') || '').trim();

    if (honeypot) {
      setStatus('success');
      resetForm(form);
      return;
    }

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      setStatus('error');
      setErrorMsg(t('contact.error', 'Email settings are missing. Please check the EmailJS environment variables.'));
      return;
    }

    try {
      setStatus('sending');
      setErrorMsg('');
      await emailjs.sendForm(serviceId, templateId, form, publicKey);
      setStatus('success');
      resetForm(form);
    } catch (error) {
      console.error('EmailJS send failed:', error);
      setStatus('error');
      setErrorMsg(t('contact.error', 'Failed to send. Please try again.'));
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-primary-50/30">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <span className="text-xs text-primary-500 font-medium uppercase tracking-widest mb-4 block">
          {t('contact.heading')}
        </span>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <h2 className="text-3xl md:text-4xl font-heading text-foreground-950 mb-5">
              {t('contact.heading')}
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-10 max-w-md">
              {t('contact.subtitle')}
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div>
                <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-600 mb-1.5">
                  {t('contact.full_name')}
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  value={formValues.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  placeholder={t('contact.full_name_placeholder')}
                  className="w-full px-4 py-3 rounded-lg border border-foreground-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-600 mb-1.5">
                  {t('contact.email')}
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={formValues.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  placeholder={t('contact.email_placeholder')}
                  className="w-full px-4 py-3 rounded-lg border border-foreground-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="contact-phone" className="block text-xs font-medium text-foreground-600 mb-1.5">
                  {t('contact.phone')}
                </label>
                <input
                  id="contact-phone"
                  type="tel"
                  name="phone_number"
                  value={formValues.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  required
                  placeholder={t('contact.phone_placeholder')}
                  className="w-full px-4 py-3 rounded-lg border border-foreground-200 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="contact-datetime" className="block text-xs font-medium text-foreground-600 mb-1.5">
                  {t('contact.datetime')}
                </label>
                <input
                  id="contact-datetime"
                  type="datetime-local"
                  name="date_time"
                  value={formValues.datetime}
                  onChange={(e) => handleChange('datetime', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-foreground-200 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="contact-service" className="block text-xs font-medium text-foreground-600 mb-1.5">
                  {t('contact.service')}
                </label>
                <select
                  id="contact-service"
                  name="service"
                  value={formValues.service}
                  onChange={(e) => handleChange('service', e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-foreground-200 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                >
                  <option value="">{t('contact.service_placeholder')}</option>
                  <option value="content">{t('contact.service_content')}</option>
                  <option value="live">{t('contact.service_live')}</option>
                  <option value="commerce">{t('contact.service_commerce')}</option>
                  <option value="shop">{t('contact.service_creator')}</option>
                  <option value="other">{t('contact.service_other')}</option>
                </select>
              </div>

              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />

              <button
                type="submit"
                disabled={status === 'sending'}
                className="px-7 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 disabled:bg-foreground-300 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 mt-2"
              >
                {status === 'sending' ? t('contact.sending', 'Sending...') : t('contact.send')}
                <i className="ri-arrow-right-up-line"></i>
              </button>

              {status === 'success' && (
                <p className="text-accent-600 text-sm font-medium">{t('contact.success')}</p>
              )}
              {status === 'error' && (
                <p className="text-primary-500 text-sm">{errorMsg}</p>
              )}
            </form>
          </div>

          <div className="lg:pt-16">
            <div className="bg-background-50 rounded-2xl border border-foreground-100/60 p-8 md:p-10">
              <div className="mb-8">
                <h4 className="text-xs font-medium text-primary-500 uppercase tracking-wider mb-3">
                  {t('contact.address_label')}
                </h4>
                <p className="text-sm text-foreground-800 leading-relaxed mb-1">
                  {t('contact.address')}
                </p>
                <p className="text-xs text-foreground-500">{t('contact.postal')}</p>
              </div>

              <div className="mb-8">
                <a
                  href="https://d-fruit.net/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-sm text-primary-500 underline hover:text-primary-700 transition-colors cursor-pointer"
                >
                  {t('contact.website')}
                </a>
              </div>

              <div>
                <h4 className="text-xs font-medium text-primary-500 uppercase tracking-wider mb-4">
                  {t('contact.opening_hours')}
                </h4>
                <div className="flex flex-col gap-2">
                  {['mon', 'tue', 'wed', 'thu', 'fri'].map((day) => (
                    <div key={day} className="flex items-center justify-between text-sm">
                      <span className="text-foreground-600">{t(`contact.${day}`)}</span>
                      <span className="text-foreground-800 font-medium">{t('contact.hours')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
